/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';
import MDCheckbox from 'components/MDCheckbox';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  name: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function SupplierBank() {
  const mod = 'supplier_bank';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalDel, setModalDel] = useState(false);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.id`), align: 'center' },
    { id: 'tb.name', label: t(`attr.firstname`), align: 'left' },
    { id: 'tb.enable', label: t(`attr.enable`), align: 'center' },
    { id: 'tb.created_at', label: t(`attr.created_at`), align: 'center' },
    { id: 'tb.updated_at', label: t(`attr.updated_at`), align: 'center' },
    { id: 'edit', label: '', align: 'center', sort: false },
    { id: 'delete', label: '', align: 'center', sort: false },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const open = (item) => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);

    FetchData('GET', `/${mod}/${item.id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setModalEdit(true);

        setLoading(false);
      }
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      name: attr.name,
      enable: attr.enable,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const edit = () => {
    setLoading(true);
    setModalEdit(false);

    FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
      name: attr.name,
      enable: attr.enable,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        setModalEdit(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = () => {
    setLoading(true);
    setModalDel(false);

    FetchData('DELETE', `/${mod}/${attr.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const enableDisplay = (inp) => {
    if (inp === 'Y') return <Icon color='info'>verified</Icon>;
    return <Icon color='error'>not_interested</Icon>;
  };

  const btnEdit = (item) => {
    return (
      <MDButton variant='text' color='success' size='small' iconOnly onClick={() => open(item)}>
        <Icon>border_color</Icon> {t(`element.edit`)}
      </MDButton>
    );
  };

  const btnDel = (item) => {
    return (
      <MDButton
        variant='text'
        color='error'
        size='small'
        iconOnly
        onClick={() => {
          setConfirm(false);
          setAttr(item);
          setModalDel(true);
        }}
      >
        <Icon>delete</Icon> {t(`element.delete`)}
      </MDButton>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{row.id}</TableCell>
                      <TableCell align='left'>{row.name}</TableCell>
                      <TableCell align='center'>{enableDisplay(row.enable)}</TableCell>
                      <TableCell align='center'>{Func.dateTimeDisplay(row.created_at)}</TableCell>
                      <TableCell align='center'>{Func.dateTimeDisplay(row.updated_at)}</TableCell>
                      <TableCell align='center'>{btnEdit(row)}</TableCell>
                      <TableCell align='center'>{btnDel(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label={`${t(`attr.name`)} *`}
            onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            value={attr.name}
            variant='standard'
            fullWidth
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDCheckbox
            label={`${t(`attr.enable`)}`}
            checked={attr.enable === 'Y'}
            onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 'Y' : 'N' })}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='xs' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog open={modalEdit} maxWidth='xs' keepMounted aria-describedby='dialog-edit' fullWidth>
        <DialogTitle>
          {t(`element.edit`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalEdit(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='success' onClick={editConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderDelete = () => {
    return (
      <Dialog
        open={modalDel}
        onClose={() => setModalDel(false)}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-delete'
        fullWidth
      >
        <DialogTitle>
          {t(`element.delete`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalDel(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText p={3}>{t(`alert.confirm_delete_title`)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setModalDel(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='gradient' color='error' onClick={del}>
                  <Icon>delete</Icon>&nbsp;{t(`element.deleteconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='error' onClick={() => setConfirm(true)}>
              <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.purchase`), t(`menu.supplier_bank`)]} />
      {renderList()}
      {renderAdd()}
      {renderEdit()}
      {renderDelete()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default SupplierBank;
